// Eventos do Google Analytics 4 (gtag.js). Onde o GA4 tem evento recomendado
// (login, sign_up, add_to_cart, purchase...) usamos o nome dele, para cair
// nos relatórios prontos de e-commerce; o resto é evento personalizado.
// Valores chegam em centavos (como no carrinho) e vão em reais para o GA.

export const GA_ID = process.env.NEXT_PUBLIC_GA_ID ?? "G-BD0FMGHFCE";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export type ItemAnalytics = {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
};

// Mesmo comportamento do snippet oficial: empilha no dataLayer e o gtag.js
// processa quando carregar (eventos disparados antes do script não se perdem)
function gtag(..._args: any[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // o gtag.js exige o objeto arguments, não um array
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export const emReais = (centavos?: number) =>
  Math.round(Number(centavos ?? 0)) / 100;

function evento(nome: string, params: Record<string, any> = {}) {
  gtag("event", nome, params);
}

// Para quando a página vai ser trocada logo em seguida (window.location):
// espera o GA confirmar o envio, com limite para nunca travar o usuário
function eventoAntesDeSair(nome: string, params: Record<string, any> = {}) {
  return new Promise<void>((resolve) => {
    const limite = setTimeout(resolve, 800);
    evento(nome, {
      ...params,
      event_callback: () => {
        clearTimeout(limite);
        resolve();
      },
    });
  });
}

// Itens do carrinho (formato que vem da API em getCart)
export function itensDoCarrinho(dados: any[] = []): ItemAnalytics[] {
  return dados.map((item) => ({
    item_id: String(item.lote?.id ?? item.lote_id ?? item.id ?? ""),
    item_name: item.lote?.nome ?? item.nome ?? "Ingresso",
    item_category: item.evento?.nome,
    item_variant: item.area?.nome,
    price: emReais(item.valor),
    quantity: Number(item.quantidade ?? 1),
  }));
}

const somaItens = (itens: ItemAnalytics[]) =>
  Math.round(itens.reduce((t, i) => t + i.price * i.quantity, 0) * 100) / 100;

// ---------------------------------------------------------------------------
// Carrinho abandonado: o navegador não sabe quando alguém "desistiu"; aqui
// marcamos quem sai do site ou do checkout com itens no carrinho. Para
// análise fina use também o funil add_to_cart → begin_checkout → purchase.
// ---------------------------------------------------------------------------
const CHAVE_CARRINHO = "ga_carrinho";
const CHAVE_ABANDONADO = "ga_carrinho_abandonado";

function lerCarrinho(): ItemAnalytics[] {
  try {
    return JSON.parse(sessionStorage.getItem(CHAVE_CARRINHO) ?? "[]");
  } catch {
    return [];
  }
}

function salvarCarrinho(itens: ItemAnalytics[]) {
  try {
    const comQuantidade = itens.filter((i) => i.quantity > 0);
    if (comQuantidade.length) {
      sessionStorage.setItem(CHAVE_CARRINHO, JSON.stringify(comQuantidade));
    } else {
      sessionStorage.removeItem(CHAVE_CARRINHO);
    }
  } catch {}
}

function somarNoCarrinho(item: ItemAnalytics, sinal: 1 | -1) {
  const itens = lerCarrinho();
  const existente = itens.find((i) => i.item_id === item.item_id);
  if (existente) {
    existente.quantity += sinal * item.quantity;
  } else if (sinal > 0) {
    itens.push({ ...item });
  }
  salvarCarrinho(itens);
}

const assinatura = (itens: ItemAnalytics[]) =>
  itens.map((i) => `${i.item_id}:${i.quantity}`).sort().join("|");

// Saindo do checkout para essas páginas ainda é parte da compra
// (completar perfil, login vindo do carrinho, voltar ao evento)
const ROTAS_DO_FUNIL = ["/checkout", "/dashboard/perfil", "/auth", "/evento"];

export const rotaDoFunil = (url: string) =>
  ROTAS_DO_FUNIL.some((r) => url.startsWith(r));

let ignorarSaida = false;

// Saída logo a seguir é do próprio sistema (reload/redirect); vale por
// pouco tempo para não esconder um abandono de verdade depois
function ignorarSaidaPorInstantes() {
  ignorarSaida = true;
  setTimeout(() => (ignorarSaida = false), 3000);
}

const analytics = {
  definirUsuario(userId?: string | number | null) {
    // só o id interno (nunca e-mail/CPF: o GA proíbe dado pessoal)
    gtag("set", { user_id: userId ? String(userId) : null });
  },

  login(metodo = "email") {
    ignorarSaidaPorInstantes(); // em seguida vem um window.location
    return eventoAntesDeSair("login", { method: metodo });
  },

  cadastro(metodo = "email") {
    ignorarSaidaPorInstantes(); // em seguida vem um window.location
    return eventoAntesDeSair("sign_up", { method: metodo });
  },

  adicionarAoCarrinho(item: ItemAnalytics) {
    somarNoCarrinho(item, 1);
    evento("add_to_cart", {
      currency: "BRL",
      value: somaItens([item]),
      items: [item],
    });
  },

  removerDoCarrinho(item: ItemAnalytics) {
    somarNoCarrinho(item, -1);
    evento("remove_from_cart", {
      currency: "BRL",
      value: somaItens([item]),
      items: [item],
    });
  },

  // Carrinho completo vindo da API (telas do checkout): substitui o acumulado
  acompanharCarrinho(dados: any[]) {
    salvarCarrinho(itensDoCarrinho(dados));
  },

  // Antes de um reload feito pelo próprio sistema (não é abandono)
  ignorarProximaSaida() {
    ignorarSaidaPorInstantes();
  },

  carrinhoAbandonado(origem: string) {
    if (ignorarSaida) return;
    const itens = lerCarrinho();
    if (!itens.length) return;

    // um disparo por carrinho (F5 ou idas e voltas não repetem)
    try {
      const atual = assinatura(itens);
      if (sessionStorage.getItem(CHAVE_ABANDONADO) === atual) return;
      sessionStorage.setItem(CHAVE_ABANDONADO, atual);
    } catch {}

    evento("carrinho_abandonado", {
      currency: "BRL",
      value: somaItens(itens),
      items: itens,
      origem,
      // a página pode estar fechando: envia mesmo assim
      transport_type: "beacon",
    });
  },

  iniciarCheckout(dados: any[], valorCentavos: number) {
    evento("begin_checkout", {
      currency: "BRL",
      value: emReais(valorCentavos),
      items: itensDoCarrinho(dados),
    });
  },

  // Pedido criado no gateway (pagamento pode ainda estar pendente, ex.: PIX)
  vendaConcluida(p: {
    pedido: string;
    valorCentavos: number;
    tipoPagamento: string;
    dados: any[];
  }) {
    salvarCarrinho([]);
    evento("venda_concluida", {
      transaction_id: p.pedido,
      currency: "BRL",
      value: emReais(p.valorCentavos),
      payment_type: p.tipoPagamento,
      items: itensDoCarrinho(p.dados),
    });
  },

  // Pagamento aprovado
  vendaSucesso(p: {
    pedido: string;
    valorCentavos: number;
    tipoPagamento: string;
    dados: any[];
  }) {
    evento("purchase", {
      transaction_id: p.pedido,
      currency: "BRL",
      value: emReais(p.valorCentavos),
      payment_type: p.tipoPagamento,
      items: itensDoCarrinho(p.dados),
    });
  },

  // Pagamento recusado ou com erro (3DS, gateway, antifraude...)
  vendaFracasso(p: {
    motivo: string;
    etapa: string;
    tipoPagamento: string;
    valorCentavos?: number;
    pedido?: string;
  }) {
    evento("venda_fracasso", {
      currency: "BRL",
      value: emReais(p.valorCentavos),
      payment_type: p.tipoPagamento,
      etapa: p.etapa,
      // parâmetros de texto no GA4 vão até 100 caracteres
      motivo: String(p.motivo ?? "").slice(0, 100),
      transaction_id: p.pedido,
    });
  },

  // Usuário desistiu ou a cobrança foi cancelada
  vendaCancelada(p: {
    motivo: string;
    tipoPagamento?: string;
    valorCentavos?: number;
    pedido?: string;
  }) {
    evento("venda_cancelada", {
      currency: "BRL",
      value: emReais(p.valorCentavos),
      payment_type: p.tipoPagamento,
      motivo: String(p.motivo ?? "").slice(0, 100),
      transaction_id: p.pedido,
    });
  },

  recuperarSenha() {
    evento("recuperar_senha");
  },

  senhaRedefinida() {
    return eventoAntesDeSair("senha_redefinida");
  },
};

export default analytics;
